/**
 * highlights.js - highlight rules for the Projects and Opportunities tables
 * (Sharif, 10/9/26: "highlight any row or column they need, e.g. highlighted
 * when zero"). Saved with the view. PURE - the tables hand in the raw value
 * of a column for a row (a number, a date string, or text).
 *
 * A rule = { col, op, value?, color, font?, bold?, scope: 'cell' | 'row' }.
 *   color  the fill: a preset key, a custom '#RRGGBB', or 'none'
 *   font   the text colour (round 2, 10/9/26): a FONT_COLORS key, '#RRGGBB'
 *          or 'none'; rules saved before it have only a fill and still work
 *   bold   bold text
 * A rule needs a fill or a text colour; `off: true` keeps it but does not
 * apply it (round 4). The first matching rule of each scope wins; on its own cell a cell rule's settings beat the row rule's.
 * server/routes/tableViews.js accepts the same ops and colours.
 */
import { dayKey } from '../ui/DateFilterPopover';
import { localDay as ymd } from '../../utils/localDay';

// Light fills that keep dark text readable.
export const HIGHLIGHT_COLORS = {
  yellow: { label: 'yellow', fill: '#FEF08A' },
  green: { label: 'green', fill: '#BBF7D0' },
  red: { label: 'red', fill: '#FECACA' },
  blue: { label: 'blue', fill: '#BFDBFE' },
  orange: { label: 'orange', fill: '#FED7AA' },
  purple: { label: 'purple', fill: '#E9D5FF' },
  gray: { label: 'gray', fill: '#E5E7EB' },
};

// Text colours: strong enough to read on white and on the light fills.
export const FONT_COLORS = {
  red: { label: 'red', fill: '#B91C1C' },
  green: { label: 'green', fill: '#15803D' },
  blue: { label: 'blue', fill: '#1D4ED8' },
  orange: { label: 'orange', fill: '#C2410C' },
  purple: { label: 'purple', fill: '#7E22CE' },
  gray: { label: 'gray', fill: '#4B5563' },
  black: { label: 'black', fill: '#111827' },
};

const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Row hover (round 3, 10/9/26: "the whole row is highlighted when hovered
 * over any column ... an option to customize as well"). A view's hover =
 * { on, color } - color a HIGHLIGHT_COLORS key or '#RRGGBB'. On by default in
 * the subtle green the tables always used.
 */
export const DEFAULT_HOVER = { on: true, color: '#D1FAE5' };
export const hoverFill = (hover) => {
  const c = (hover && hover.color) || DEFAULT_HOVER.color;
  if (HEX.test(c)) return c.toUpperCase();
  return HIGHLIGHT_COLORS[c] ? HIGHLIGHT_COLORS[c].fill : DEFAULT_HOVER.color;
};
/** The table's class and style for its row hover (index.css .tv-hover / .tv-nohover). */
export const hoverProps = (hover) =>
  hover && hover.on === false
    ? { className: 'tv-nohover', style: {} }
    : { className: 'tv-hover', style: { '--row-hover': hoverFill(hover) } };
/** A rule's fill / text colour as '#RRGGBB', or null for none. An old rule with no colour at all is yellow. */
export const fillOf = (rule) => {
  const c = rule.color === undefined && !rule.font ? 'yellow' : rule.color;
  if (HEX.test(c || '')) return c.toUpperCase();
  return HIGHLIGHT_COLORS[c] ? HIGHLIGHT_COLORS[c].fill : null;
};
export const fontOf = (rule) => {
  const c = rule.font;
  if (HEX.test(c || '')) return c.toUpperCase();
  return FONT_COLORS[c] ? FONT_COLORS[c].fill : null;
};

// op -> { label (in the picker), kinds it applies to, input: what value it needs }
export const HIGHLIGHT_OPS = {
  zero: { label: 'is zero', kinds: ['number'], input: null },
  blank: { label: 'is blank', kinds: ['number', 'date', 'text'], input: null },
  zeroOrBlank: { label: 'is zero or blank', kinds: ['number'], input: null },
  eq: { label: '=', kinds: ['number'], input: 'number' },
  ne: { label: '≠', kinds: ['number'], input: 'number' },
  gt: { label: '>', kinds: ['number'], input: 'number' },
  gte: { label: '≥', kinds: ['number'], input: 'number' },
  lt: { label: '<', kinds: ['number'], input: 'number' },
  lte: { label: '≤', kinds: ['number'], input: 'number' },
  contains: { label: 'contains', kinds: ['text'], input: 'text' },
  equals: { label: 'is exactly', kinds: ['text'], input: 'text' },
  before: { label: 'is before', kinds: ['date'], input: 'date' },
  after: { label: 'is after', kinds: ['date'], input: 'date' },
  lastDays: { label: 'is in the last N days', kinds: ['date'], input: 'number' },
  overdue: { label: 'is overdue (before today)', kinds: ['date'], input: null },
  always: { label: 'always (highlight the column)', kinds: ['number', 'date', 'text'], input: null },
};

export const opsFor = (kind) => Object.keys(HIGHLIGHT_OPS).filter((op) => HIGHLIGHT_OPS[op].kinds.includes(kind || 'text'));

const isBlank = (v) => v == null || String(v).trim() === '';
const toNum = (v) => {
  if (typeof v === 'number') return v;
  if (isBlank(v)) return NaN;
  const s = String(v).replace(/[$,%\s]/g, '');
  return s === '' ? NaN : Number(s);
};

/** Does this rule match this raw value? `today` is injectable for tests. */
export function matchRule(rule, v, today = new Date()) {
  const n = toNum(v);
  const x = Number(rule.value);
  switch (rule.op) {
    case 'always':
      return true;
    case 'blank':
      return isBlank(v);
    case 'zero':
      return n === 0;
    case 'zeroOrBlank':
      return isBlank(v) || n === 0;
    case 'eq':
      return !Number.isNaN(n) && n === x;
    case 'ne':
      return !Number.isNaN(n) && n !== x;
    case 'gt':
      return !Number.isNaN(n) && n > x;
    case 'gte':
      return !Number.isNaN(n) && n >= x;
    case 'lt':
      return !Number.isNaN(n) && n < x;
    case 'lte':
      return !Number.isNaN(n) && n <= x;
    case 'contains':
      return (
        !isBlank(rule.value) &&
        String(v ?? '')
          .toLowerCase()
          .includes(String(rule.value).toLowerCase())
      );
    case 'equals':
      return (
        String(v ?? '')
          .trim()
          .toLowerCase() ===
        String(rule.value ?? '')
          .trim()
          .toLowerCase()
      );
    default:
      break;
  }
  const day = isBlank(v) ? null : dayKey(v);
  if (!day) return false;
  const t = ymd(today);
  switch (rule.op) {
    case 'before':
      return day < rule.value;
    case 'after':
      return day > rule.value;
    case 'overdue':
      return day < t;
    case 'lastDays': {
      const from = new Date(today);
      from.setDate(from.getDate() - (Number(rule.value) || 0));
      return day >= ymd(from) && day <= t;
    }
    default:
      return false;
  }
}

/**
 * The look of one row: { row: look | null, cells: { colId: look } }, a look
 * being { fill, font, bold } (only what a rule sets). valueOf(colId) gives the
 * row's raw value for a column. Rules on a hidden column still apply (a row
 * rule on a hidden PO $ still paints the row).
 */
export function rowHighlights(rules, valueOf, today) {
  const out = { row: null, cells: {} };
  for (const r of rules || []) {
    if (r.off) continue; // switched off in Customize, kept for later
    if (r.scope === 'row' ? out.row : out.cells[r.col]) continue;
    if (!matchRule(r, valueOf(r.col), today)) continue;
    const look = {};
    if (fillOf(r)) look.fill = fillOf(r);
    if (fontOf(r)) look.font = fontOf(r);
    if (r.bold) look.bold = true;
    if (r.scope === 'row') out.row = look;
    else out.cells[r.col] = look;
  }
  return out;
}

/** The look of one cell: its row's, with its own cell rule's settings on top. */
export const cellLook = (hl, col) => (hl && (hl.row || hl.cells[col]) ? { ...hl.row, ...hl.cells[col] } : null);

/**
 * Inline style for a highlighted <td>. The Projects rows paint every cell
 * from --health-tint with !important (index.css, the health flags), so the
 * fill goes through that variable too; the Opportunities rows read background.
 */
export const highlightStyle = (look) => {
  if (!look) return undefined;
  const st = {};
  if (look.fill) Object.assign(st, { background: look.fill, '--health-tint': look.fill, '--health-hover': look.fill });
  if (look.font) st.color = look.font;
  if (look.bold) st.fontWeight = 700;
  return st;
};

const fmtDay = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  return m ? `${+m[2]}/${+m[3]}/${m[1]}` : s;
};
const nameOf = (c, presets) => (HEX.test(c || '') ? c.toUpperCase() : presets[c] ? presets[c].label : null);

/** "Highlight row yellow when PO $ is zero" - the rule in words. */
export function ruleSentence(rule, labelOf = (c) => c) {
  const label = labelOf(rule.col);
  const fill = rule.color === undefined && !rule.font ? 'yellow' : nameOf(rule.color, HIGHLIGHT_COLORS);
  const font = nameOf(rule.font, FONT_COLORS);
  // "yellow", "red text", "yellow, red text," - the comma pair reads as an aside
  const parts = [fill, font && `${font} text`, rule.bold && 'bold'].filter(Boolean);
  const look = parts.length > 1 ? `${parts.join(', ')},` : parts[0] || '';
  const what = rule.scope === 'row' ? 'row' : 'cell';
  if (rule.op === 'always') return `Highlight the ${label} column ${look.replace(/,$/, '')}`;
  const v = rule.value;
  const cond =
    {
      zero: 'is zero',
      blank: 'is blank',
      zeroOrBlank: 'is zero or blank',
      eq: `= ${v}`,
      ne: `≠ ${v}`,
      gt: `> ${v}`,
      gte: `≥ ${v}`,
      lt: `< ${v}`,
      lte: `≤ ${v}`,
      contains: `contains "${v}"`,
      equals: `is "${v}"`,
      before: `is before ${fmtDay(v)}`,
      after: `is after ${fmtDay(v)}`,
      lastDays: `is in the last ${v} days`,
      overdue: 'is overdue',
    }[rule.op] || rule.op;
  return `Highlight ${what} ${look} when ${label} ${cond}`;
}
